import React, { Fragment, useCallback, useContext } from "react";
import {
  any,
  set,
  path,
  lensPath,
  equals,
  lensProp,
  view,
  remove,
  includes,
} from "ramda";
import { notBlank, notEmpty } from "../../util/ext";
import { useCategories } from "../vocabularyServices";
import { MediaContext } from "./media-context";
import { FixedSizeList } from "react-window";
import { matchSorter } from "match-sorter";

import Autocomplete from "@material-ui/lab/Autocomplete";
import Grid from "@material-ui/core/Grid";
import ListItemText from "@material-ui/core/ListItemText";
import Skeleton from "@material-ui/lab/Skeleton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";

import AudienceSelect from "./AudienceSelect";
import ColumnHeader from "../content/edit/ColumnHeader";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 80;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemSize * itemCount;
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const EditCategoriesColumn = props => {
  const context = useContext(MediaContext);
  const categories = useCategories();
  const { className, showAudience, showFourCategories, validator } = props;

  const getOptionDisabled = option =>
    any(category => option.id === category.id, context.media.bisacCategories);

  const handleCategoryChange = useCallback(
    path => (event, value) => {
      if (notBlank(value)) {
        context.setMedia(prev => set(lensPath(path), value, prev));
      } else {
        const [list, index] = path;
        context.setMedia(prev =>
          set(
            lensProp(list),
            remove(index, 1, view(lensProp(list), prev)),
            prev,
          ),
        );
      }
    },
    [context],
  );

  const handleTextChange = useCallback(
    path => event => {
      const value = event.target.value;
      context.setMedia(prev => set(lensPath(path), value, prev));
    },
    [context],
  );

  const filterOptions = (options, { inputValue }) =>
    matchSorter(options, inputValue, {
      keys: ["headingDescription", "bisacCode"],
    });

  return (
    <div className={className}>
      <Grid item xs={12}>
        <ColumnHeader header="Categories" paddingBottom={0} />
      </Grid>

      {notEmpty(context.media) && (
        <Fragment>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disableClearable={includes(context.media.type, [
                  "BOOK",
                  "AUDIOBOOK",
                ])}
                options={categories.data}
                value={path(["bisacCategories", 0], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                filterOptions={filterOptions}
                renderOption={cat => (
                  <Tooltip
                    title={cat.headingDescription}
                    placement="bottom-start"
                  >
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={cat.headingDescription}
                      secondary={cat.bisacCode}
                    />
                  </Tooltip>
                )}
                ListboxComponent={ListboxComponent}
                getOptionDisabled={getOptionDisabled}
                onChange={handleCategoryChange(["bisacCategories", 0])}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      required={
                        includes(context.media.type, ["BOOK", "AUDIOBOOK"]) ||
                        includes(context.media.mediaType, ["anthology"])
                      }
                      type="text"
                      onBlur={() =>
                        context.setErrors(
                          validator.validateField(
                            "bisacCategories",
                            context.media.bisacCategories[0],
                          ),
                        )
                      }
                      error={validator.hasError(
                        context.errors,
                        "bisacCategories",
                      )}
                      helperText={validator.firstError(
                        context.errors,
                        "bisacCategories",
                      )}
                      name={"media-category1-" + Math.random()}
                      variant="standard"
                      label="Category 1"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                options={categories.data}
                disabled={context.media.bisacCategories.length < 1}
                value={path(["bisacCategories", 1], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                onChange={handleCategoryChange(["bisacCategories", 1])}
                filterOptions={filterOptions}
                ListboxComponent={ListboxComponent}
                renderOption={cat => (
                  <Tooltip
                    title={cat.headingDescription}
                    placement="bottom-start"
                  >
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={cat.headingDescription}
                      secondary={cat.bisacCode}
                    />
                  </Tooltip>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category2-" + Math.random()}
                      variant="standard"
                      label="Category 2"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disabled={context.media.bisacCategories.length < 2}
                autoComplete
                options={categories.data}
                value={path(["bisacCategories", 2], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                onChange={handleCategoryChange(["bisacCategories", 2])}
                filterOptions={filterOptions}
                ListboxComponent={ListboxComponent}
                renderOption={cat => (
                  <Tooltip
                    title={cat.headingDescription}
                    placement="bottom-start"
                  >
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={cat.headingDescription}
                      secondary={cat.bisacCode}
                    />
                  </Tooltip>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category3-" + Math.random()}
                      variant="standard"
                      label="Category 3"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          {showFourCategories && (
            <Grid item xs={12}>
              {categories.isLoading ? (
                <Skeleton variant="text" />
              ) : (
                <Autocomplete
                  disabled={context.media.bisacCategories.length < 3}
                  autoComplete
                  options={categories.data}
                  value={path(["bisacCategories", 3], context.media) || ""}
                  getOptionLabel={cat =>
                    notBlank(cat) ? cat.headingDescription : ""
                  }
                  getOptionSelected={(opt, val) =>
                    notBlank(val) && equals(opt.id, val.id)
                  }
                  onChange={handleCategoryChange(["bisacCategories", 3])}
                  filterOptions={filterOptions}
                  ListboxComponent={ListboxComponent}
                  renderOption={cat => (
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  )}
                  renderInput={params => {
                    params.inputProps["aria-autocomplete"] = "none";
                    params.inputProps["id"] = null;
                    return (
                      <TextField
                        {...params}
                        type="text"
                        name={"media-category4-" + Math.random()}
                        variant="standard"
                        label="Category 4"
                        fullWidth
                      />
                    );
                  }}
                />
              )}
            </Grid>
          )}
          {showAudience && (
            <Grid item xs={12}>
              <AudienceSelect
                value={path(["audienceCode"], context.media)}
                onChange={handleTextChange(["audienceCode"])}
              />
            </Grid>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default EditCategoriesColumn;
